import React, { useMemo, useState, FC } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import * as m3u8Parser from 'm3u8-parser';
import videoApi from 'src/api/video';
import { VIDEO_QUALITIES } from 'src/helpers/constants';
import { useRequest } from 'src/helpers/use';
import { swrImmutableOptions } from 'src/helpers/utils';

import { PreviewVideoPlayerStyle } from './style';

import VideoJs from '@components/molecules/videojs';
import { getVideoSetting } from '@helpers/videoSetting';
import { ISubtitle, VideoSetting } from '@interfaces/learning';
import { VideoFileType } from '@common/type/Filetype';

export type PreviewVideoPlayerProps = {
  videoUrl: string;
  thumbUrl?: string;
  subtitles?: ISubtitle[];
  className?: string;
  videoType?: VideoFileType;
  disabledVideoQualities?: boolean;
};

const PreviewVideoPlayer: FC<PreviewVideoPlayerProps> = ({ videoUrl, thumbUrl, videoType, subtitles = [], disabledVideoQualities = false, className = '' }) => {
  const [currentVideoSetting, setCurrentVideoSetting] = useState<VideoSetting>();

  // Only fetch playlist data if qualities are enabled and URL looks like HLS playlist
  const isHlsPlaylist = videoUrl?.includes('.m3u8') || videoUrl?.includes('playlist');
  const shouldFetchPlaylist = !disabledVideoQualities && isHlsPlaylist;

  const { data: playlistData } = useRequest<string>(
    shouldFetchPlaylist ? videoUrl : null,
    shouldFetchPlaylist ? () => videoApi.fetchPlaylistData(videoUrl) : undefined,
    swrImmutableOptions,
  );

  const videoQualities = useMemo(() => {
    if (disabledVideoQualities) {
      return [];
    }

    const qualities: { name: string; url: string }[] = [
      {
        name: VIDEO_QUALITIES.AUTO,
        url: videoUrl,
      },
    ];

    if (playlistData && isHlsPlaylist) {
      try {
        const parser = new m3u8Parser.Parser();
        parser.push(playlistData);
        parser.end();

        parser.manifest?.playlists?.forEach((playlist: { attributes: { NAME: string }; uri: string }) => {
          qualities.push({
            name: playlist.attributes.NAME,
            url: playlist.uri,
          });
        });
      } catch (error) {
        console.warn('Failed to parse HLS playlist:', error);
      }
    }

    const videoSetting = getVideoSetting();
    if (videoSetting) {
      setCurrentVideoSetting(videoSetting);
    }

    return qualities;
  }, [playlistData, videoUrl, disabledVideoQualities, isHlsPlaylist]);

  return (
    <PreviewVideoPlayerStyle>
      {videoUrl ? (
        <VideoJs
          thumbUrl={thumbUrl}
          videoUrl={videoUrl}
          subtitles={subtitles}
          videoQualities={videoQualities}
          textTrackLabel={currentVideoSetting?.textTrackLabel}
          volume={currentVideoSetting?.volume}
          quality={currentVideoSetting?.quality}
          isMuted={currentVideoSetting?.isMuted}
          playbackRate={currentVideoSetting?.playbackRate}
          className={className}
          videoType={videoType}
        />
      ) : (
        <div className="shimmer-wrapper" />
      )}
    </PreviewVideoPlayerStyle>
  );
};

export default PreviewVideoPlayer;
